import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsSupportChatTestFeatureFlagOn,
  selectUserProjectIsUuidBased,
} from 'core/userContext';
import { BUTTON_VARIANTS, Button, ChatButton } from 'ui/button';
import { Takeover } from 'ui/workflows/takeover';
import noop from 'utils/noop';
import { PaginationFooter } from 'ui/paginationFooter';
import React, { useMemo } from 'react';
import { InputSearch } from 'ui/inputs';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { NodeData } from 'modules/workflows/types';
import { useRestQuery } from 'utils/hooks/api';
import { getWorkflow } from 'modules/workflows/ducks';
import { Export24 } from 'ui/icons';
import { downloadAsCsvFileWithHeaders } from 'utils/csv';
import { addPositiveToast } from 'modules/toastContainer';
import { NodeVisitorsTable } from './NodeVisitorsTable';
import {
  TableWrapper,
  StyledTableToolbar,
  StyledVisitor,
  NodeVisitorsTakeoverWrapper,
  StyledToolbarActions,
} from './NodeVisitorsTakeover.style';
import {
  DownloadType,
  filterDownIfAnyValuesIncludeFilterString,
  formatDataForCSV,
  getTableToolbarTitle,
  nodeVisitorsFileName,
  nodeVisitorsHeaders,
  paginateResults,
  url,
} from './utils';
import { useSortRows } from 'ui/table/hooks/useSortRows';
import { NodeVisitorRowResponse } from './NodeVisitors.types';

export type NodeVisitorsTakeoverProps = {
  onClose: () => void;
  nodeData: NodeData;
  isOverlay: boolean;
};

export const NodeVisitorsTakeover = ({
  nodeData,
  onClose = noop,
  isOverlay = true,
}: NodeVisitorsTakeoverProps) => {
  const dispatch = useDispatch();
  const [filter, setFilter] = React.useState('');
  const [sortBy, setSortBy] = React.useState('email');
  const [pagingDetails, setPagingDetails] = React.useState({ page: 1, pageSize: 10 });
  const [sortDirection, setSortDirection] = React.useState(SORT_DIRECTIONS.DESCENDING);
  const workflow = useSelector(getWorkflow);
  const isUuidBased = useSelector(selectUserProjectIsUuidBased);
  const isSupportChatTestEnabled = useSelector(selectIsSupportChatTestFeatureFlagOn);
  const { id: nodeId, title, numUsers } = nodeData;

  const { isLoading, data } = useRestQuery(
    url(DownloadType.JSON, nodeId, workflow?.workflowType),
    undefined,
    { refetchOnWindowFocus: false },
  );

  const responseData = (data || []).map((item: NodeVisitorRowResponse) => {
    const { externalUserKeys, ...restItem } = item;
    const { email, userId } = externalUserKeys || {};
    const primaryValue = isUuidBased && !!userId ? userId : email;
    return { ...restItem, email, primaryValue, userId, secondaryValue: userId };
  });

  const sortedData = useSortRows(responseData, sortBy, sortDirection);

  const filteredData = useMemo(() => {
    const filteredRows = filter.length
      ? filterDownIfAnyValuesIncludeFilterString(sortedData || [], filter)
      : sortedData;
    return filteredRows;
  }, [sortedData, filter]);

  const pagedData = useMemo(() => {
    return paginateResults(filteredData, pagingDetails);
  }, [pagingDetails, filteredData]);

  const tableToolbarTitle = useMemo(() => {
    return getTableToolbarTitle(numUsers, responseData?.length, nodeId);
  }, [numUsers, responseData, nodeId]);

  const onExport = () => {
    downloadAsCsvFileWithHeaders({
      data: formatDataForCSV(responseData),
      headers: nodeVisitorsHeaders,
      fileName: nodeVisitorsFileName(workflow?.name || 'journey'),
    });
    dispatch(addPositiveToast('Successfully exported journey visitors'));
  };

  return (
    <Takeover
      isLoading={isLoading}
      isOverlay={isOverlay}
      onClose={onClose}
      noPadding
      title={`Users currently in "${title}" tile`}
      HeaderBarContent={isSupportChatTestEnabled && <ChatButton />}
      Body={
        <NodeVisitorsTakeoverWrapper>
          <StyledTableToolbar>
            <StyledVisitor>{tableToolbarTitle}</StyledVisitor>
            <StyledToolbarActions>
              <Button variant={BUTTON_VARIANTS.GHOST} onClick={onExport}>
                <Export24 />
                Export
              </Button>
              <InputSearch
                id="visitor-search"
                value={filter}
                onChange={(value: string) => setFilter(value)}
              />
            </StyledToolbarActions>
          </StyledTableToolbar>
          <TableWrapper>
            <NodeVisitorsTable
              type={nodeData.type}
              data={pagedData}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />
            <PaginationFooter
              currentPage={pagingDetails.page}
              itemsPerPage={pagingDetails.pageSize}
              numItems={filteredData?.length}
              maxPages={10}
              onUpdate={(e) => setPagingDetails(e)}
              pageSizes={[10, 20, 30, 40]}
              isItemCountShown={true}
            />
          </TableWrapper>
        </NodeVisitorsTakeoverWrapper>
      }
    ></Takeover>
  );
};
